import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Constants
const MAX_APPOINTMENTS_LIMIT = 5000;

// Utility function to create auth config
const createAuthConfig = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    "Content-Type": "application/json"
  }
});

// Utility function to handle error responses
const handleApiError = (error, defaultMessage) => {
  const errorMessage = error.response?.data?.message ||
                      error.response?.data?.response_message ||
                      defaultMessage;
  return {
    status: "error",
    message: errorMessage
  };
};

// Fetch all appointments for filtering (similar to practitioner's getAppointmentCounter)
export const getAppointmentCounter = createAsyncThunk(
  "appointmentDetail/getAppointmentCounter",
  async (payload, { rejectWithValue }) => {
    try {
      const offset = payload?.offset || 0;
      const limit = payload?.limit || MAX_APPOINTMENTS_LIMIT;
      const orderBy = payload?.orderBy || 'id';
      const direction = payload?.direction || 'DESC';

      const response = await axios.post(
        `${API_BASE_URL}/counsellor/appointments?offset=${offset}&limit=${limit}&orderBy=${orderBy}&direction=${direction}`,
        payload,
        createAuthConfig()
      );

      return {
        status: "success",
        data: response.data
      };
    } catch (error) {
      return rejectWithValue(handleApiError(error, "Failed to fetch appointment data"));
    }
  }
);

// Fetch appointment detail data (similar to practitioner's getAppointmentDetailData)
export const getAppointmentDetailData = createAsyncThunk(
  "appointmentDetail/getAppointmentDetailData",
  async (appointmentId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/appointment/details/${appointmentId}`,
        createAuthConfig()
      );

      return {
        status: "success",
        data: response.data
      };
    } catch (error) {
      return rejectWithValue(handleApiError(error, "Failed to fetch appointment details"));
    }
  }
);

const appointmentDetailSlice = createSlice({
  name: "appointmentDetail",
  initialState: {
    // All appointments data for filtering
    appointmentCounter: {
      data: null,
      loading: false,
      error: null
    },
    // Specific appointment detail data
    appointmentDetail: {
      detail: null,
      loading: false,
      error: null
    }
  },
  reducers: {
    clearAppointmentDetail: (state) => {
      state.appointmentDetail.detail = null;
      state.appointmentDetail.error = null;
    },
    clearAppointmentCounter: (state) => {
      state.appointmentCounter.data = null;
      state.appointmentCounter.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Get Appointment Counter
      .addCase(getAppointmentCounter.pending, (state) => {
        state.appointmentCounter.loading = true;
        state.appointmentCounter.error = null;
      })
      .addCase(getAppointmentCounter.fulfilled, (state, action) => {
        state.appointmentCounter.loading = false;
        if (action.payload.status === "success") {
          state.appointmentCounter.data = action.payload.data;
        } else {
          state.appointmentCounter.error = action.payload.message;
        }
      })
      .addCase(getAppointmentCounter.rejected, (state, action) => {
        state.appointmentCounter.loading = false;
        state.appointmentCounter.error = action.payload?.message || "Failed to fetch appointment data";
      })

      // Get Appointment Detail Data
      .addCase(getAppointmentDetailData.pending, (state) => {
        state.appointmentDetail.loading = true;
        state.appointmentDetail.error = null;
      })
      .addCase(getAppointmentDetailData.fulfilled, (state, action) => {
        state.appointmentDetail.loading = false;
        if (action.payload.status === "success") {
          state.appointmentDetail.detail = action.payload.data;
        } else {
          state.appointmentDetail.error = action.payload.message;
        }
      })
      .addCase(getAppointmentDetailData.rejected, (state, action) => {
        state.appointmentDetail.loading = false;
        state.appointmentDetail.error = action.payload?.message || "Failed to fetch appointment details";
      });
  },
});

export const { clearAppointmentDetail, clearAppointmentCounter } = appointmentDetailSlice.actions;
export default appointmentDetailSlice.reducer;